import type {
  BaseRule,
  Experiment,
  PageRules,
  RedirectVariantConfig,
  Variant,
  Workspace,
} from '../../../../types'
import { normalizeRuleForEditor } from '../targeting/utils/ruleMutations'

export const REDIRECT_SIMPLE_RULE_ID = 'redirect:simple-original'
export const REDIRECT_SIMPLE_VALUE_ID = 'redirect:simple-url'
const REDIRECT_PREVIEW_RULE_VARIABLES = new Set([
  'url',
  'host',
  'domain',
  'hash',
  'query',
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_term',
  'utm_content',
])

export const getRedirectPreviewablePageRules = (rules: PageRules = {}): PageRules => {
  return Object.fromEntries(
    Object.entries(rules).filter(([, rule]) => (
      REDIRECT_PREVIEW_RULE_VARIABLES.has(rule.variable)
    )),
  )
}

export const hasUnsupportedRedirectPreviewRules = (rules: PageRules = {}): boolean => {
  return Object.values(rules).some((rule) => (
    !REDIRECT_PREVIEW_RULE_VARIABLES.has(rule.variable)
  ))
}

export const isAbsoluteHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim())
    return (url.protocol === 'http:' || url.protocol === 'https:')
      && !url.username
      && !url.password
  } catch {
    return false
  }
}

export const isRedirectLocation = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed || trimmed.startsWith('//')) return false
  return trimmed.startsWith('/') || isAbsoluteHttpUrl(trimmed)
}

export const normalizeSimpleTargetUrl = (value: string): string | null => {
  if (!isAbsoluteHttpUrl(value)) return null
  const url = new URL(value.trim())
  const pathname = url.pathname.replace(/\/+$/, '') || '/'
  return `${url.host}${pathname}`
}

export const buildSimpleUrlRules = (value: string): PageRules => {
  const normalizedUrl = normalizeSimpleTargetUrl(value)
  if (!normalizedUrl) return {}

  const rules: PageRules = {
    [REDIRECT_SIMPLE_RULE_ID]: {
      id: REDIRECT_SIMPLE_RULE_ID,
      variable: 'url',
      condition: 'equals',
      option: 'any_of',
      values: { [REDIRECT_SIMPLE_VALUE_ID]: normalizedUrl },
    },
  }

  const url = new URL(value.trim())
  if (url.hash) {
    rules['redirect:simple-hash'] = {
      id: 'redirect:simple-hash',
      variable: 'hash',
      condition: 'equals',
      option: 'any_of',
      values: { 'redirect:simple-hash-value': url.hash },
    }
  }

  for (const [index, [key, queryValue]] of [...url.searchParams.entries()].entries()) {
    const ruleId = `redirect:simple-query-${index}`
    rules[ruleId] = {
      id: ruleId,
      variable: 'query',
      condition: 'equals',
      option: 'any_of',
      values: { [key]: queryValue },
    }
  }

  return rules
}

export const isSimpleUrlRules = (rules: PageRules = {}): boolean => {
  const ruleIds = Object.keys(rules)
  return ruleIds.length > 0 && ruleIds.every((id) => id.startsWith('redirect:simple-'))
}

export const getSimpleUrlFromRules = (rules: PageRules = {}): string => {
  const urlRule = rules[REDIRECT_SIMPLE_RULE_ID]
  const urlValue = urlRule ? Object.values(urlRule.values || {})[0] : ''
  if (!urlValue) return ''

  const query = new URLSearchParams()
  Object.values(rules).forEach((rule) => {
    if (rule.variable !== 'query') return
    Object.entries(rule.values || {}).forEach(([key, value]) => query.append(key, value))
  })
  const hashRule = rules['redirect:simple-hash']
  const hash = hashRule ? Object.values(hashRule.values || {})[0] || '' : ''
  const queryString = query.toString()

  return `https://${urlValue}${queryString ? `?${queryString}` : ''}${hash}`
}

export const getRedirectSourceUrls = (rules: PageRules = {}): string[] => {
  const values = Object.values(rules)
    .filter((rule) => rule.variable === 'url')
    .flatMap((rule) => Object.values(rule.values || {}))
    .map((value) => value.trim())
    .filter(Boolean)

  return [...new Set(values)]
}

export const hasRedirectUrlRule = (rules: PageRules = {}): boolean => {
  return Object.values(rules).some((rule) => (
    rule.variable === 'url'
    && Object.values(rule.values || {}).some((value) => value.trim().length > 0)
  ))
}

const normalizeRedirectSourceLocation = (value: string): string | null => {
  const trimmed = value.trim()
  if (isRedirectLocation(trimmed)) return trimmed

  return isAbsoluteHttpUrl(`https://${trimmed}`) ? `https://${trimmed}` : null
}

export const getExactRedirectSourceUrls = (rules: PageRules = {}): string[] => {
  const values = Object.values(rules)
    .filter((rule) => rule.variable === 'url' && rule.condition === 'equals')
    .flatMap((rule) => Object.values(rule.values || {}))
    .map(normalizeRedirectSourceLocation)
    .filter((value): value is string => Boolean(value))

  return [...new Set(values)]
}

const redirectLocationParts = (value: string): { origin: string | null; path: string } | null => {
  const trimmed = value.trim()
  if (!isRedirectLocation(trimmed)) return null

  try {
    const isRelative = trimmed.startsWith('/')
    const url = new URL(trimmed, 'https://redirect-target.invalid')
    return {
      origin: isRelative ? null : url.origin.toLowerCase(),
      path: `${url.pathname}${url.search}${url.hash}`,
    }
  } catch {
    return null
  }
}

const redirectMappingCoversSource = (mappingSource: string, target: string): boolean => {
  const source = redirectLocationParts(mappingSource)
  const targetParts = redirectLocationParts(target)
  if (!source || !targetParts || source.path !== targetParts.path) return false
  return source.origin === null || source.origin === targetParts.origin
}

export const getMappedRedirectCoverageError = (
  experiment: Experiment,
  variants: Variant[],
): string | null => {
  const mappedVariants = variants.filter(
    (variant) => !variant.control && variant.redirect_config?.strategy === 'mapped',
  )
  if (mappedVariants.length === 0) return null

  const urlRules = Object.values(experiment.page_rules || {}).filter((rule) => rule.variable === 'url')
  if (urlRules.some((rule) => rule.condition !== 'equals')) {
    return 'Map Each URL requires Original URL rules using equals.'
  }

  const targets = getExactRedirectSourceUrls(experiment.page_rules)
  if (targets.length === 0) {
    return 'Map Each URL requires at least one exact Original URL.'
  }

  const hasUncoveredTarget = mappedVariants.some((variant) => {
    const mappings = variant.redirect_config?.mappings || []
    return targets.some((target) => !mappings.some((mapping) => (
      redirectMappingCoversSource(mapping.source_url, target)
    )))
  })

  return hasUncoveredTarget ? 'Map each Original URL to a destination before starting.' : null
}

export const getSuggestedPreviewUrl = (
  experiment: Experiment,
  workspace?: Workspace | null,
): string => {
  if (experiment.redirect_settings?.preview_url && isAbsoluteHttpUrl(experiment.redirect_settings.preview_url)) {
    return experiment.redirect_settings.preview_url
  }

  if (isSimpleUrlRules(experiment.page_rules)) {
    return getSimpleUrlFromRules(experiment.page_rules)
  }

  const verifiedHostnames = (workspace?.hostnames || [])
    .filter((hostname) => hostname.verified && !hostname.disabled)
    .map((hostname) => hostname.hostname.replace(/^https?:\/\//i, '').replace(/\/+$/, ''))

  return verifiedHostnames[0] ? `https://${verifiedHostnames[0]}` : ''
}

export const getFirstVerifiedHostnameHomepageUrl = (
  workspace?: Workspace | null,
): string => {
  const hostname = (workspace?.hostnames || []).find(
    (item) => item.verified && !item.disabled && item.hostname.trim(),
  )?.hostname.trim()
  if (!hostname) return ''

  try {
    const url = new URL(/^https?:\/\//i.test(hostname) ? hostname : `https://${hostname}`)
    return `${url.origin}/`
  } catch {
    return ''
  }
}

const REDIRECT_PREVIEW_POSITIVE_CONDITIONS = new Set([
  'equals',
  'contains',
  'starts_with',
  'ends_with',
])

const getFirstRuleValue = (rule?: BaseRule): string => {
  if (!rule || !REDIRECT_PREVIEW_POSITIVE_CONDITIONS.has(rule.condition || '')) return ''
  return Object.values(rule.values || {}).map(String).find((value) => value.trim())?.trim() || ''
}

const applyUrlRuleValue = (url: URL, value: string): URL => {
  if (isAbsoluteHttpUrl(value)) return new URL(value.trim())

  const trimmed = value.trim()
  if (/^[^/?#]+\.[^/?#]+(?:[/?#]|$)/i.test(trimmed)) {
    try {
      return new URL(`https://${trimmed}`)
    } catch {
      return url
    }
  }

  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  try {
    return new URL(path, url.origin)
  } catch {
    return url
  }
}

/**
 * Builds a best-effort preview URL for web/PWA views where there is no active
 * storefront tab. The normal preview validator remains the source of truth.
 */
export const getRedirectRulePreviewUrl = (
  experiment: Experiment,
  workspace?: Workspace | null,
): string => {
  const verifiedHostname = (workspace?.hostnames || []).find(
    (hostname) => hostname.verified && !hostname.disabled && hostname.hostname?.trim(),
  )?.hostname
  if (!verifiedHostname) return ''

  const normalizedHostname = verifiedHostname
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '')

  let candidate: URL
  try {
    candidate = new URL(`https://${normalizedHostname}/`)
  } catch {
    return ''
  }

  const rules = Object.values(experiment.page_rules || {})
  const urlValue = getFirstRuleValue(rules.find((rule) => rule.variable === 'url'))
  if (urlValue) candidate = applyUrlRuleValue(candidate, urlValue)

  rules.forEach((rule) => {
    const value = getFirstRuleValue(rule)
    if (!value) return

    if (rule.variable === 'query') {
      const [key] = Object.keys(rule.values || {})
      if (key) candidate.searchParams.set(key, value)
      return
    }

    if (rule.variable.startsWith('utm_')) {
      candidate.searchParams.set(rule.variable, value)
      return
    }

    if (rule.variable === 'hash') {
      candidate.hash = value.startsWith('#') ? value : `#${value}`
    }
  })

  return candidate.toString()
}

export const getSuggestedPreviewUrlFromRules = (
  experiment: Experiment,
): string => isSimpleUrlRules(experiment.page_rules)
  ? getSimpleUrlFromRules(experiment.page_rules)
  : ''

export const getRedirectConfigError = (config: RedirectVariantConfig): string | null => {
  switch (config.strategy) {
    case 'single':
      return isRedirectLocation(config.destination_url || '')
        ? null
        : 'Enter a valid destination URL or path.'

    case 'mapped': {
      const mappings = config.mappings || []
      if (!mappings.length) return 'Add at least one URL mapping.'
      if (mappings.some((mapping) => !isRedirectLocation(mapping.source_url))) {
        return 'Every original URL must be valid.'
      }
      if (mappings.some((mapping) => !isRedirectLocation(mapping.destination_url))) {
        return 'Enter a valid destination for every original URL.'
      }
      return null
    }

    case 'append_path': {
      const value = (config.append_value || '').trim()
      return value.startsWith('/')
        && !value.startsWith('//')
        && !value.includes('?')
        && !value.includes('#')
        ? null
        : 'Enter a path beginning with / without parameters or a hash.'
    }

    case 'append_parameter': {
      const value = (config.append_value || '').trim().replace(/^\?/, '')
      if (!value || value.includes('#')) return 'Enter one or more valid URL parameters.'
      const parameters = new URLSearchParams(value)
      return [...parameters.keys()].some((key) => !key.trim())
        ? 'Enter one or more valid URL parameters.'
        : null
    }
  }
}

export const isRedirectTestSetupReady = (
  experiment: Experiment,
  variants: Variant[],
): boolean => {
  const controlCount = variants.filter((variant) => variant.control).length
  const treatments = variants.filter((variant) => !variant.control)
  const destinationsAreReady = treatments.length > 0 && treatments.every((variant) => (
    variant.redirect_config && getRedirectConfigError(variant.redirect_config) === null
  ))
  const targetingIsReady = experiment.redirect_settings?.targeting_mode === 'any'
    || hasRedirectUrlRule(experiment.page_rules)

  return controlCount === 1 && destinationsAreReady && targetingIsReady
}

export const isRedirectTestWalkthroughComplete = (
  experiment: Experiment,
  variants: Variant[],
): boolean => {
  if (experiment.redirect_settings?.setup_completed !== undefined) {
    return experiment.redirect_settings.setup_completed
  }
  if (isRedirectTestSetupReady(experiment, variants)) return true

  // Redirect tests created before setup_completed existed are considered
  // configured once they have a valid saved treatment destination.
  const controlCount = variants.filter((variant) => variant.control).length
  const hasSavedDestination = variants.some((variant) => (
    !variant.control
    && variant.redirect_config
    && getRedirectConfigError(variant.redirect_config) === null
  ))
  return controlCount === 1 && hasSavedDestination
}

export type RedirectSetupRouteDecision = {
  experimentId: string,
  showOnboarding: boolean,
}

export const resolveRedirectSetupRouteDecision = (
  experimentId: string,
  current: RedirectSetupRouteDecision | null,
  setupReady: boolean,
): RedirectSetupRouteDecision => {
  if (current?.experimentId === experimentId) return current

  return {
    experimentId,
    showOnboarding: !setupReady,
  }
}

export const hasRedirectConfigInput = (config: RedirectVariantConfig): boolean => {
  if (config.strategy === 'single') return Boolean(config.destination_url?.trim())
  if (config.strategy === 'mapped') {
    return Boolean(config.mappings?.some((mapping) => mapping.destination_url.trim()))
  }
  return Boolean(config.append_value?.trim())
}

const toSafeUrl = (value: string, source: URL): URL | null => {
  if (!isRedirectLocation(value)) return null
  try {
    return new URL(value.trim(), source.origin)
  } catch {
    return null
  }
}

const preserveSourceUrlParts = (source: URL, destination: URL, maintain: boolean) => {
  if (!maintain) return

  for (const key of new Set(source.searchParams.keys())) {
    if (destination.searchParams.has(key)) continue
    source.searchParams.getAll(key).forEach((value) => destination.searchParams.append(key, value))
  }
  if (!destination.hash && source.hash) destination.hash = source.hash
}

const sourceMatches = (sourceValue: string, candidate: URL): boolean => {
  const source = toSafeUrl(sourceValue, candidate)
  if (!source) return false
  if (/^https?:\/\//i.test(sourceValue) && source.origin.toLowerCase() !== candidate.origin.toLowerCase()) {
    return false
  }
  if (source.pathname !== candidate.pathname) return false

  for (const key of new Set(source.searchParams.keys())) {
    const expected = source.searchParams.getAll(key)
    const actual = candidate.searchParams.getAll(key)
    if (expected.some((value) => !actual.includes(value))) return false
  }
  return !sourceValue.includes('#') || source.hash === candidate.hash
}

export const resolveRedirectPreview = (
  config: RedirectVariantConfig | null | undefined,
  sourceValue: string,
  maintainParametersAndHash = true,
): string | null => {
  if (!config || !isAbsoluteHttpUrl(sourceValue)) return null
  const source = new URL(sourceValue.trim())
  let destination: URL | null = null

  if (config.strategy === 'single') {
    destination = toSafeUrl(config.destination_url || '', source)
  } else if (config.strategy === 'mapped') {
    const mapping = (config.mappings || []).find((item) => sourceMatches(item.source_url, source))
    destination = mapping ? toSafeUrl(mapping.destination_url, source) : null
  } else if (config.strategy === 'append_path') {
    if (getRedirectConfigError(config)) return null
    destination = new URL(source.href)
    const basePath = destination.pathname === '/' ? '' : destination.pathname.replace(/\/+$/, '')
    destination.pathname = `${basePath}${config.append_value}`
    if (!maintainParametersAndHash) {
      destination.search = ''
      destination.hash = ''
    }
  } else if (config.strategy === 'append_parameter') {
    if (getRedirectConfigError(config)) return null
    destination = new URL(source.href)
    if (!maintainParametersAndHash) {
      destination.search = ''
      destination.hash = ''
    }
    const parameters = new URLSearchParams((config.append_value || '').replace(/^\?/, ''))
    for (const key of new Set(parameters.keys())) {
      destination.searchParams.delete(key)
      parameters.getAll(key).forEach((value) => destination?.searchParams.append(key, value))
    }
  }

  if (!destination) return null
  preserveSourceUrlParts(source, destination, maintainParametersAndHash)
  return destination.href
}

export const getRedirectTreatmentVariants = (variants: Variant[]): Variant[] => {
  return variants.filter((variant) => !variant.control)
}

export const toRuleItems = (rules: PageRules = {}): BaseRule[] => {
  return Object.entries(rules).map(([id, rule]) => normalizeRuleForEditor({ ...rule, id }))
}
